import * as angular from 'angular';
import { NetworkPointResource } from 'app/_resources/network-point.resource';

(function () {
    'use strict';

    /**
     * @ngdoc directive
     * @name networkPointSearch
     * @memberof app
     *
     * @restrict E
     * @scope
     *
     * @description
     * A versatile directive for the network point search that appears at several places to search for network points.
     * Also used to filter by network points.
     *
     * @param {boolean=} separateFavorites At its core it's responsible to include favorite network point in the whole
     *  process as they would not be included by default. They will be treated like the non-favorite results,
     *  but separately and listed at the top. Defaults to `false`, if it's `!== true`.
     * @param {string=} placeholder An optional placeholder text for the input field.
     * @param {function=} submitSelection Function to be called when the network point search dropdown dialog
     *  is submitted. As a parameter the selected network points will be passed on.
     * @param {boolean=} singleSelection Will hide checkboxes before each network point, because they are not selectable,
     *  as only one network point can be submitted. The submission takes place immediately upon clicking on the
     *  network point. Defaults to `false`, if it's `!== true`.
     * @param {Object[]} preSelected Without any search being done, a result list will already be shown
     *  with selected network points from this array. An Array of `NetworkPointDto`s.
     * @param {string=} mode From the constant {@link NETWORK_POINT_SEARCH_MODE}. Based on the mode, the buttons
     *  at the bottom of the dialog will change their labels and submit methods.
     * @param {boolean=} dropdownPersist If `true`, the dropdown won't be toggled on click and blur,
     *  but be always visible in any case. Defaults to `false`, if it's `!== true`.
     * @param {boolean=} hideCheckboxes If `true`, hides all checkboxes. Useful, when just a list of network point
     *  should be shown without any functionality. Defaults to `false`, if it's `!== true`.
     * @param {boolean=} hideButtons Hide the cancel and submit buttons at the bottom.
     *  Defaults to `false`, if it's `!== true`.
     * @param {boolean=} noLeftOverflow If the network point search is situated far on the left side of the page,
     *  setting this to true will make sure, it won't be placed out of the boundaries of the screen.
     *  Defaults to `false`, if it's `!== true`.
     * @param {string=} inputAddon Value of {@link networkPointSearchService.INPUT_ADDON} to display a bootstrap
     *  input addon for the input field. Defaults to `'NONE'`.
     * @param {string=} search The value of the input field can be set with this.
     * @param {string=} isFilterFcfs If `true`, hides filter the network points accordingly with CR293 (BTCPKP-6144).
     * Defaults to `false`, if it's `!== true`.
     * @param {string=} detailLevel Value of {@link DetailLevel} to display the graph and the actions available on a given
     * @param {string=} filterMarket filter for 'PRIMARY' or 'SECONDARY'
     * @param {string=} hideCubicMeterPoints If `true`, hides Cm3/h network points accordingly. Defaults to `false`, if it's `!== true`.
     * @link {AccessPoint}. Defaults to `'MINIMAL'`.
     * * @param {string=} bundleType Value of {@link BundleType} to filter for bundled/unbundled/all
     * @link {AccessPoint}. Defaults to `'ALL'`.
     *
     * @author flzemke.btc
     */
    angular.module('app').directive('networkPointSearch', networkPointSearch);

    function networkPointSearch() {
        return {
            restrict: 'E',
            scope: {
                separateFavorites: '=?',
                placeholder: '@',
                placeholderModel: '=?',
                submitSelection: '&',
                onQuickfilter: '&?',
                singleSelection: '=?',
                preSelected: '=?',
                mode: '=?',
                dropdownPersist: '=?',
                hideCheckboxes: '=?',
                hideButtons: '=?',
                noLeftOverflow: '=?',
                inputAddon: '=?',
                search: '=?',
                filterMarketArea: '=',
                filterMarketAreaNames: '=',
                filterMarketAreaReverse: '=',
                filterTso: '=',
                filterTsoReverse: '=',
                isFilterFcfs: '=?',
                detailLevel: '=?',
                bundleType: '@',
                filterDirection: '@',
                filterMarket: '@?',
                filterConnectionPointType: '=?',
                filterFlowUnit: '=?',
                alignmentMode: '=?',
                allowFutureForTso: '@?',
                ownInactivePointsOnly: '=?',
                hideCubicMeterPoints: '=?'
            },
            template: require('app/_directives/network-point-search.directive.html'),
            link: linkFunction,
            controller: NetworkPointSearchController,
            controllerAs: 'npSearchCtrl'
        };

        function linkFunction(scope, elem, attrs) {
            // attrs is "NOT_SUPPORTED" when used from angular 7 context via UpgradeComponent
            // in that case, angular does type checking and stuff and we dont need to do this
            if (attrs !== 'NOT_SUPPORTED') {
                attrs.separateFavorites =
                    attrs.separateFavorites !== 'true'
                        ? 'false'
                        : attrs.separateFavorites;
                attrs.placeholder = attrs.placeholder ? attrs.placeholder : '';
                attrs.dropdownPersist =
                    attrs.dropdownPersist !== 'true'
                        ? 'false'
                        : attrs.dropdownPersist;
                attrs.hideCheckboxes =
                    attrs.hideCheckboxes !== 'true'
                        ? 'false'
                        : attrs.hideCheckboxes;
                attrs.hideButtons =
                    attrs.hideButtons !== 'true' ? 'false' : attrs.hideButtons;
                attrs.noLeftOverflow =
                    attrs.noLeftOverflow !== 'true'
                        ? 'false'
                        : attrs.noLeftOverflow;
                attrs.singleSelection =
                    attrs.singleSelection !== 'true'
                        ? 'false'
                        : attrs.singleSelection;
                attrs.isFilterFcfs =
                    attrs.isFilterFcfs !== 'true'
                        ? 'false'
                        : attrs.isFilterFcfs;
                attrs.hideCubicMeterPoints =
                    attrs.hideCubicMeterPoints !== 'true'
                        ? 'false'
                        : attrs.hideCubicMeterPoints;
            } else {
                // fix stuff gone wrong before
                scope.npSearchCtrl.placeholder = scope.placeholder;
                scope.npSearchCtrl.placeholderDefaultValue = scope.placeholder;
            }
        }

        function NetworkPointSearchController(
            $scope,
            $rootScope,
            $q,
            $filter,
            platformSettingsService,
            NetworkPoint: NetworkPointResource,
            networkPointSearchService,
            NETWORK_POINT_SEARCH_MODE,
            authService
        ) {
            var vm = this;

            vm.DIRECTION = platformSettingsService.DIRECTION;

            /**
             * The search term from the input field
             *
             * @type {string}
             */
            vm.search = $scope.search || '';
            /**
             * Used to negate when cancelling the dropdown.
             *
             * @type {boolean}
             */
            vm.uiShowSuggestions = true;
            /**
             * The list of auto suggestions for the user.
             *
             * @type {Array}
             */
            vm.suggestions = [];
            /**
             * Like {@link vm.suggestions} but just for network points that are favorites of the user.
             *
             * @type {Array}
             */
            vm.favoriteSuggestions = [];
            /**
             * The user will be given a notification, if the number of results exceeds this value
             * and every record exceeding the value will be cut from the list.
             *
             * @type {number}
             */

            vm.searchText = vm.search;
            vm.placeholderDefaultValue = null;

            if (!angular.isUndefined($scope.placeholderModel)) {
                vm.placeholderDefaultValue = angular.copy(
                    $scope.placeholderModel
                );
            } else {
                vm.placeholderDefaultValue = angular.copy($scope.placeholder);
            }
            vm.placeholder = vm.placeholderDefaultValue;
            vm.searchFocused = false;

            $scope.$watch(
                function () {
                    return vm.searchText;
                },
                function () {
                    if (vm.searchText !== '') {
                        vm.search = vm.searchText;
                    }
                    onChange();
                }
            );

            $scope.$watch(
                function () {
                    return $scope.placeholderModel;
                },
                function () {
                    if (
                        angular.isUndefined($scope.placeholderModel) ||
                        $scope.placeholderModel === null ||
                        $scope.placeholderModel === ''
                    ) {
                        vm.placeholder = vm.placeholderDefaultValue;
                    }
                    onChange();
                }
            );

            vm.MAX_SEARCH_RESULT = 100;
            vm.userQuickFilters = [];
            vm.staticOverview = [];
            vm.showFavoritesOnly = false;
            vm.NETWORK_POINT_SEARCH_MODE = NETWORK_POINT_SEARCH_MODE;
            vm.INPUT_ADDON = networkPointSearchService.INPUT_ADDON;
            vm.DETAIL_LEVEL = networkPointSearchService.DETAIL_LEVEL;
            vm.onChange = onChange;
            vm.cancel = cancel;
            vm.alignment = $scope.alignmentMode
                ? $scope.alignmentMode
                : 'right';
            vm.cancelAndBroadcast = cancelAndBroadcast;
            vm.submitNetworkPoints = submitNetworkPoints;
            vm.submitNetworkPoint = submitNetworkPoint;
            vm.submitQuickFilter = submitQuickFilter;
            vm.editNetworkPoints = editNetworkPoints;
            vm.selectAll = selectAll;
            vm.deselectAll = deselectAll;
            vm.onToggle = onToggle;
            vm.quickFilterMatchesSearch = quickFilterMatchesSearch;
            vm.widthOfScrollbar = widthOfScrollbar;
            vm.submitNetworkPointsToStaticOverview =
                submitNetworkPointsToStaticOverview;
            vm.uiLoadingInProgress = false;
            vm.uiNotFoundBecauseIsAlreadyFavorite = false;
            vm.isLoggedIn = authService.isLoggedIn();

            $scope.mode = networkPointSearchService.defaultModeAttr(
                $scope.mode
            );
            $scope.inputAddon = networkPointSearchService.defaultInputAddonAttr(
                $scope.inputAddon
            );
            $scope.detailLevel =
                networkPointSearchService.defaultDetailLevelAttr(
                    $scope.detailLevel
                );

            // Preselect network points if the directive was parameterized accordingly.
            if (!angular.isUndefined($scope.preSelected)) {
                networkPointSearchService.addCheckedFlag(
                    $scope.preSelected,
                    true
                );
            }

            if (
                $scope.inputAddon === vm.INPUT_ADDON.QUICK_FILTER &&
                vm.isLoggedIn
            ) {
                NetworkPoint.queryQuickFilters(
                    undefined,
                    function (res) {
                        vm.userQuickFilters = res;
                    },
                    function () {
                        // TODO Handle
                    }
                );
            }

            //if search field is populated with value from previous wizard step for example,
            //onChange() is triggered to perform search and display item if search box receives focus
            if (vm.searchText !== '') {
                onChange(function () {
                    selectAll();
                });
            }

            /**
             * Listens to changes on input with model-bound {@link vm.search}.
             */
            function onChange(cb?) {
                if (vm.search.length < 3) {
                    return;
                }

                vm.uiLoadingInProgress = true;

                var param: any = {
                    mode: 'filtered',
                    isFilterFcfs: $scope.isFilterFcfs,
                    hideCubicMeterPoints: $scope.hideCubicMeterPoints,
                    infix: vm.search
                };

                if (
                    !angular.isUndefined($scope.filterTso) &&
                    $scope.filterTso.length > 0
                ) {
                    param.tsoNames = $scope.filterTso;
                }

                if (!angular.isUndefined($scope.filterTsoReverse)) {
                    param.filterTsoReverse = $scope.filterTsoReverse;
                }

                if (!angular.isUndefined($scope.filterMarketAreaReverse)) {
                    param.filterMarketAreaReverse =
                        $scope.filterMarketAreaReverse;
                }

                if (
                    !angular.isUndefined($scope.filterDirection) &&
                    $scope.filterDirection.length > 0
                ) {
                    param.directions = $scope.filterDirection;
                }

                if (!angular.isUndefined($scope.detailLevel)) {
                    param.detailLevel = $scope.detailLevel;
                }

                if (!angular.isUndefined($scope.filterMarket)) {
                    param.marketFilter = $scope.filterMarket;
                }

                if (!angular.isUndefined($scope.filterConnectionPointType)) {
                    param.connectionPointType =
                        $scope.filterConnectionPointType;
                }

                if (!angular.isUndefined($scope.filterFlowUnit)) {
                    param.flowUnit = $scope.filterFlowUnit;
                }

                if (
                    !angular.isUndefined($scope.bundleType) &&
                    $scope.bundleType !==
                        networkPointSearchService.BUNDLE_TYPE.ALL
                ) {
                    param.bundleType = $scope.bundleType;
                }

                if (
                    !angular.isUndefined($scope.filterMarketArea) &&
                    $scope.filterMarketArea.length > 0
                ) {
                    param.marketAreaFilter = $scope.filterMarketArea;
                }

                if (
                    !angular.isUndefined($scope.filterMarketAreaNames) &&
                    $scope.filterMarketAreaNames.length > 0
                ) {
                    param.filterMarketAreaNames = $scope.filterMarketAreaNames;
                }

                if (!angular.isUndefined($scope.allowFutureForTso)) {
                    param.allowFutureForTso = $scope.allowFutureForTso;
                }
                if (!!$scope.ownInactivePointsOnly) {
                    param.showOnlyInactivePoints = $scope.ownInactivePointsOnly;
                }

                var queryFiltered = NetworkPoint.query(param, function (res) {
                    vm.suggestions = res;
                    networkPointSearchService.populateSuggestionsWithModelAttribute(
                        vm.showFavoritesOnly,
                        $scope.separateFavorites,
                        $scope.preSelected,
                        vm.suggestions,
                        vm.favoriteSuggestions
                    );
                });

                var queryFilteredFavorites: any = {};

                var paramFavorties = angular.copy(param);
                paramFavorties.favorites = 'true';

                if ($scope.separateFavorites) {
                    queryFilteredFavorites = NetworkPoint.query(
                        paramFavorties,
                        function (res) {
                            vm.favoriteSuggestions = res;
                            networkPointSearchService.populateSuggestionsWithModelAttribute(
                                vm.showFavoritesOnly,
                                $scope.separateFavorites,
                                $scope.preSelected,
                                vm.suggestions,
                                vm.favoriteSuggestions
                            );
                        }
                    );
                }

                $q.all([
                    queryFiltered.$promise,
                    queryFilteredFavorites.$promise
                ]).then(function () {
                    if (
                        !vm.suggestions.length &&
                        $scope.mode ===
                            vm.NETWORK_POINT_SEARCH_MODE.NEW_FAVORITE
                    ) {
                        NetworkPoint.isFavorite(
                            { identifierOrName: vm.search },
                            function (res) {
                                vm.uiNotFoundBecauseIsAlreadyFavorite =
                                    res.isFavorite === true;
                                vm.uiLoadingInProgress = false;
                            },
                            function () {
                                vm.uiNotFoundBecauseIsAlreadyFavorite = false;
                                vm.uiLoadingInProgress = false;
                            }
                        );
                    } else {
                        vm.uiLoadingInProgress = false;
                    }

                    if (angular.isFunction(cb)) {
                        cb();
                    }
                });
            }

            /**
             * Calls {@link $scope.submitSelection} passing a list of the selected network points.
             */
            function submitNetworkPoints() {
                var newNetworkPoints =
                    networkPointSearchService.getSelectedNetworkPoints(
                        vm.showFavoritesOnly,
                        $scope.separateFavorites,
                        $scope.preSelected,
                        vm.suggestions,
                        vm.favoriteSuggestions
                    );
                $scope.submitSelection({
                    selectedNetworkPoints: newNetworkPoints,
                    isFromQuickfilter: false
                });
                vm.uiShowSuggestions = false;

                var result = '';
                angular.forEach(newNetworkPoints, function (value) {
                    result += result === '' ? value.name : ', ' + value.name;
                });
                vm.searchText = '';
                vm.placeholder = result;
            }

            /**
             * Calls {@link $scope.submitSelection} passing the selected network point
             * only if @link $scope.singleSelection} is true.
             */
            function submitNetworkPoint(networkPoint) {
                if ($scope.singleSelection) {
                    vm.uiShowSuggestions = false;
                    vm.search = networkPoint.name;

                    const exposingNetworkPoint = angular.copy(networkPoint);
                    delete exposingNetworkPoint._checked;
                    $scope.submitSelection({
                        selectedNetworkPoints: exposingNetworkPoint,
                        isFromQuickfilter: false
                    });

                    vm.searchText = '';
                    vm.placeholder = networkPoint.name;
                }
            }

            function submitQuickFilter(quickFilter) {
                if (!vm.quickFilterMatchesSearch(quickFilter)) {
                    return;
                }

                vm.uiShowSuggestions = false;
                if ($scope.onQuickfilter) {
                    $scope.onQuickfilter({ quickFilter });
                }
                $scope.submitSelection({
                    selectedNetworkPoints: quickFilter.networkPointList,
                    isFromQuickfilter: true
                });
            }

            function submitNetworkPointsToStaticOverview() {
                var selectedNetworkPoints = [];
                var tmpNetworkPoint;

                var i;
                for (i = 0; i < vm.suggestions.length; i++) {
                    if (vm.suggestions[i]._checked === true) {
                        tmpNetworkPoint =
                            networkPointSearchService.removeCheckedFlagAddFavoriteFlag(
                                vm.suggestions[i],
                                false
                            );
                        selectedNetworkPoints.push(tmpNetworkPoint);
                    }
                }

                var k;
                for (k = 0; k < vm.favoriteSuggestions.length; k++) {
                    if (vm.favoriteSuggestions[k]._checked === true) {
                        tmpNetworkPoint =
                            networkPointSearchService.removeCheckedFlagAddFavoriteFlag(
                                vm.favoriteSuggestions[k],
                                true
                            );
                        selectedNetworkPoints.push(tmpNetworkPoint);
                    }
                }

                vm.staticOverview.push.apply(
                    vm.staticOverview,
                    selectedNetworkPoints
                );
                vm.staticOverview = $filter('unique')(vm.staticOverview, 'id');
                $scope.submitSelection({
                    selectedNetworkPoints: selectedNetworkPoints,
                    isFromQuickfilter: true
                });
                vm.uiShowSuggestions = false;
            }

            function editNetworkPoints() {
                var newNetworkPoints =
                    networkPointSearchService.getSelectedNetworkPoints(
                        vm.showFavoritesOnly,
                        $scope.separateFavorites,
                        $scope.preSelected,
                        vm.suggestions,
                        vm.favoriteSuggestions
                    );
                $scope.submitSelection({
                    selectedNetworkPoints: newNetworkPoints,
                    isFromQuickfilter: false
                });
            }

            /**
             * Close the dialog and deselect all network points as a reset.
             */
            function cancel() {
                vm.uiShowSuggestions = false;
                deselectAll();
            }

            /**
             * Like {@link cancel} but also broadcasts the event.
             */
            function cancelAndBroadcast() {
                cancel();
                $rootScope.$broadcast(
                    networkPointSearchService.BROADCAST.CANCEL
                );
            }

            /**
             * Selects all network points by changing <code>_checked</code> to true in {@link vm.suggestions}
             * and {@link vm.favoriteSuggestions}.
             * It won't select non-favored network points when they are not shown
             * (<code>{@link vm.showFavoritesOnly} = false</code>).
             */
            function selectAll() {
                var networkPoints =
                    networkPointSearchService.mergeFavoriteAndNonFavoriteAndPreselectedNetworkPoints(
                        vm.showFavoritesOnly,
                        $scope.separateFavorites,
                        $scope.preSelected,
                        vm.suggestions,
                        vm.favoriteSuggestions
                    );
                networkPointSearchService.addCheckedFlag(networkPoints, true);
            }

            /**
             * Deselect all network points by changing <code>_checked</code> to false in {@link vm.suggestions}
             * and {@link vm.favoriteSuggestions}.
             * It won't deselect non-favored network points when they are not shown
             * (<code>{@link vm.showFavoritesOnly} = false</code>).
             */
            function deselectAll() {
                var networkPoints =
                    networkPointSearchService.mergeFavoriteAndNonFavoriteAndPreselectedNetworkPoints(
                        vm.showFavoritesOnly,
                        $scope.separateFavorites,
                        $scope.preSelected,
                        vm.suggestions,
                        vm.favoriteSuggestions
                    );
                networkPointSearchService.addCheckedFlag(networkPoints, false);
            }

            /**
             * Don't hide the dropdown when it's toggled.
             * Sometimes hiding is forced when closed via cancel button.
             */
            function onToggle() {
                vm.uiShowSuggestions = true;
            }

            function quickFilterMatchesSearch(quickFilter) {
                if (vm.search.length < 3) {
                    return true;
                }

                for (var i = 0; i < quickFilter.networkPointList.length; i++) {
                    if (
                        quickFilter.networkPointList[i].name.indexOf(
                            vm.search
                        ) > -1 ||
                        quickFilter.networkPointList[i].identifier.indexOf(
                            vm.search
                        ) > -1
                    ) {
                        return true;
                    }
                }

                return false;
            }

            function widthOfScrollbar() {
                return networkPointSearchService.widthOfScrollbar();
            }
        }
    }
})();
